import { useStore } from "@/stores/store";
import { useAccessControl } from "@/hooks/useAccessControl";
import type { SubscriptionStatus } from "@/hooks/subscription";
import {
  EnvelopeIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  SubscriptionStatus,
  { label: string; color: string; dotColor: string }
> = {
  active: {
    label: "Active",
    color: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20",
    dotColor: "bg-emerald-500",
  },
  in_trial: {
    label: "Trial",
    color: "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20",
    dotColor: "bg-blue-500",
  },
  non_renewing: {
    label: "Non-Renewing",
    color: "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20",
    dotColor: "bg-amber-500",
  },
  paused: {
    label: "Paused",
    color: "bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-500/10 dark:text-gray-400 dark:ring-gray-500/20",
    dotColor: "bg-gray-400",
  },
  cancelled: {
    label: "Cancelled",
    color: "bg-red-50 text-red-700 ring-red-600/10 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20",
    dotColor: "bg-red-500",
  },
  future: {
    label: "Scheduled",
    color: "bg-violet-50 text-violet-700 ring-violet-600/10 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-500/20",
    dotColor: "bg-violet-500",
  },
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateObj(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatPlanName(planId: string): string {
  return planId
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function daysUntil(date: Date): number {
  const diff = date.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: SubscriptionStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${config.color}`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
}

function DetailRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: JSX.Element;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      {icon}
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-0.5 text-sm text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Epoch-based access section (token is the source of truth)
// ---------------------------------------------------------------------------

function EpochAccessSection({
  expiresAtDate,
  isExpired,
  extendMailto,
}: {
  expiresAtDate: Date;
  isExpired: boolean;
  extendMailto: string;
}) {
  const days = daysUntil(expiresAtDate);
  const isExpiringSoon = !isExpired && days <= 7;

  const variant = isExpired
    ? "expired"
    : isExpiringSoon
      ? "warning"
      : "active";

  const palette = {
    expired: {
      bg: "bg-red-50 dark:bg-red-500/10",
      icon: <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />,
      heading: "text-red-800 dark:text-red-400",
      sub: "text-red-700 dark:text-red-300",
    },
    warning: {
      bg: "bg-amber-50 dark:bg-amber-500/10",
      icon: <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />,
      heading: "text-amber-800 dark:text-amber-400",
      sub: "text-amber-700 dark:text-amber-300",
    },
    active: {
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
      icon: <CheckCircleIcon className="h-5 w-5 text-emerald-500" />,
      heading: "text-emerald-800 dark:text-emerald-400",
      sub: "text-emerald-700 dark:text-emerald-300",
    },
  }[variant];

  const title = isExpired
    ? "Access Expired"
    : isExpiringSoon
      ? `Access Expiring Soon (${days} day${days === 1 ? "" : "s"} left)`
      : "Access Active";

  const subtitle = isExpired
    ? `Your access expired on ${formatDateObj(expiresAtDate)}`
    : `Your access is valid until ${formatDateObj(expiresAtDate)}`;

  const body = isExpired
    ? "Your access has ended. Contact our team to renew your access or subscribe to a plan."
    : "To continue using Newsquawk after your access period, contact our team or subscribe to a plan.";

  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
      <div>
        <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
          Your Access
        </h2>
        <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
          Your access is managed directly by the Newsquawk team.
        </p>
      </div>

      <div className="md:col-span-2">
        <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden">
          {/* Status header */}
          <div className={`px-5 py-4 ${palette.bg}`}>
            <div className="flex items-center gap-3">
              {palette.icon}
              <div>
                <p className={`text-sm font-semibold ${palette.heading}`}>
                  {title}
                </p>
                <p className={`mt-0.5 text-xs ${palette.sub}`}>{subtitle}</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-5 py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{body}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={extendMailto}
                className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-squawk-blue hover:bg-squawk-blue-dark-soft text-white font-semibold transition-colors"
              >
                <EnvelopeIcon className="h-4 w-4" />
                {isExpired ? "Request Access" : "Extend Access"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscription-based section (fallback when epoch is null)
// ---------------------------------------------------------------------------

function SubscriptionSection({
  subscription,
}: {
  subscription: NonNullable<ReturnType<typeof useAccessControl>["subscriptionQuery"]["data"]>;
}) {
  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
      <div>
        <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
          Subscription
        </h2>
        <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
          Your current subscription details and billing period.
        </p>
      </div>

      <div className="md:col-span-2">
        <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 dark:border-white/10 px-5 py-4">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatPlanName(subscription.plan_id)}
              </h3>
              <StatusBadge status={subscription.status} />
            </div>
            <a
              href="https://newsquawk.chargebeeportal.com/portal/v2/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-squawk-blue hover:bg-squawk-blue-dark-soft text-white font-semibold transition-colors"
            >
              <CreditCardIcon className="h-4 w-4" />
              {subscription.status === "cancelled"
                ? "Reactivate Subscription"
                : "Manage Subscription"}
            </a>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 px-5 py-3 divide-y sm:divide-y-0 divide-gray-100 dark:divide-white/5">
            <DetailRow
              icon={<CalendarDaysIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />}
              label="Current Period"
              value={
                subscription.current_period_start && subscription.current_period_end
                  ? `${formatDate(subscription.current_period_start)} - ${formatDate(subscription.current_period_end)}`
                  : "N/A"
              }
            />

            {subscription.status === "active" && subscription.current_period_end && (
              <DetailRow
                icon={<ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />}
                label="Renews On"
                value={formatDate(subscription.current_period_end)}
              />
            )}
            {subscription.status === "non_renewing" && subscription.current_period_end && (
              <DetailRow
                icon={<ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />}
                label="Access Ends"
                value={formatDate(subscription.current_period_end)}
              />
            )}
            {subscription.status === "in_trial" && subscription.trial_end && (
              <DetailRow
                icon={<ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />}
                label="Trial Ends"
                value={formatDate(subscription.trial_end)}
              />
            )}
            {subscription.status === "cancelled" && subscription.cancelled_at && (
              <DetailRow
                icon={<ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />}
                label="Cancelled On"
                value={formatDate(subscription.cancelled_at)}
              />
            )}
          </div>

          {/* Cancel reason banner */}
          {subscription.cancel_reason && (
            <div className="border-t border-gray-200 dark:border-white/10 px-5 py-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">Cancellation reason:</span>{" "}
                {subscription.cancel_reason}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// No information fallback
// ---------------------------------------------------------------------------

function NoInfoSection({ supportMailto }: { supportMailto: string }) {
  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
      <div>
        <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
          Your Access
        </h2>
        <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
          No subscription or access information was found for your account.
        </p>
      </div>

      <div className="md:col-span-2">
        <div className="rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-5 py-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No subscription or access information found. If you believe this is
            an error, please reach out to our support team.
          </p>
          <div className="mt-4">
            <a
              href={supportMailto}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-squawk-blue hover:bg-squawk-blue-dark-soft text-white font-semibold transition-colors"
            >
              <EnvelopeIcon className="h-4 w-4" />
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function Billing() {
  const tokenParsed = useStore((state: any) => state.tokenParsed);
  const { email, name } = tokenParsed || {};

  const { accessExpiresAtDate, isAccessExpired, subscriptionQuery } =
    useAccessControl();
  const { data: subscription, isLoading } = subscriptionQuery;

  // Mailto helpers
  const supportSubject = encodeURIComponent("Billing Support Request");
  const supportBody = encodeURIComponent(
    `Name: ${name || "N/A"}\nEmail: ${email || "N/A"}\n\nPlease describe your issue:\n`
  );
  const supportMailto = `mailto:support@newsquawk.com?subject=${supportSubject}&body=${supportBody}`;

  const extendSubject = encodeURIComponent("Access Extension Request");
  const extendBody = encodeURIComponent(
    `Name: ${name || "N/A"}\nEmail: ${email || "N/A"}\n\nI would like to extend my access. Please let me know the available options.\n`
  );
  const extendMailto = `mailto:support@newsquawk.com?subject=${extendSubject}&body=${extendBody}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-squawk-blue dark:border-gray-600 dark:border-t-squawk-blue" />
      </div>
    );
  }

  // ── Priority: epoch is the source of truth when present ──
  // If access_expires_at_epoch exists in the token, it overrides subscription.
  // Only when epoch is null do we fall through to the subscription record.
  const hasEpoch = accessExpiresAtDate !== null;

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/10">
      {subscription ? (
        <SubscriptionSection subscription={subscription} />
      ) : hasEpoch ? (
        <EpochAccessSection
          expiresAtDate={accessExpiresAtDate}
          isExpired={isAccessExpired}
          extendMailto={extendMailto}
        />
      ) : (
        <NoInfoSection supportMailto={supportMailto} />
      )}

      {/* ── Need Help? ── */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Need Help?
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            If you're having problems with your subscription or billing, our
            support team is here to help.
          </p>
        </div>
        <div className="self-center">
          <a
            href={supportMailto}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-white/10 text-gray-700 dark:text-white font-semibold hover:bg-gray-100 dark:hover:bg-white/20 transition-colors"
          >
            <EnvelopeIcon className="h-4 w-4" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}

export default Billing;
